import React, { useState, useCallback, useEffect, useRef } from "react"
import { Dialog, Typography, IconButton, Box } from "@material-ui/core"

import useStyles from "@/pages/Attendance/ReferralPopup/styles"
import { colors } from "@/styles/colors"
import ColorUtil from "@/utils/ColorUtil"
import {
	REFERRAL_SWAP_OUT_DURATION_IN_MS,
	REFERRAL_SWAP_IN_DURATION_IN_MS,
	REFERRAL_SWAP_GAP_IN_MS,
	stageToWidthInPixels
} from "@/pages/Attendance/ReferralPopup/constants"
import {
	ReferralStage,
	ReferralFormData,
	ReferralWalletData,
	ReferralPopupProps,
	CreateReferralPayload
} from "@/pages/Attendance/ReferralPopup/types"
import ReferralOfferScreen from "@/pages/Attendance/ReferralPopup/screens/ReferralOfferScreen"
import ReferralFormScreen from "@/pages/Attendance/ReferralPopup/screens/ReferralFormScreen"
import ReferralLinkScreen from "@/pages/Attendance/ReferralPopup/screens/ReferralLinkScreen"
import ReferralDoneScreen from "@/pages/Attendance/ReferralPopup/screens/ReferralDoneScreen"
import ReferralService from "@/services/ReferralService"
import useValidation, { ErrorType } from "@/hooks/useValidation"

type TransitionPhase = "idle" | "swapping-out" | "swapping-in"

type ModalState = {
	stage: ReferralStage
	transitionPhase: TransitionPhase
	isClosing: boolean
}

const initialFormData: ReferralFormData = {
	companyName: "",
	contactName: "",
	whatsapp: "",
	channel: "google_ads",
	payoutMethod: "cash"
}

const initialModalState: ModalState = {
	stage: "offer",
	transitionPhase: "idle",
	isClosing: false
}

const buildCreateReferralPayload = (formData: ReferralFormData, isAdmin: boolean): CreateReferralPayload => {
	if (isAdmin) {
		return {
			businessName: formData.companyName,
			contactName: formData.contactName,
			whatsapp: formData.whatsapp,
			channel: formData.channel
		}
	}

	return {
		businessName: formData.companyName,
		whatsapp: formData.whatsapp,
		payoutMethod: formData.payoutMethod
	}
}

const ReferralPopup: React.FC<ReferralPopupProps> = (props) => {
	const { open, onClose, isAdmin, referralLink } = props
	const classes = useStyles()
	const { triggerValidation, clearValidation, validation } = useValidation()

	const [modalState, setModalState] = useState<ModalState>(initialModalState)
	const { stage: currentStage, transitionPhase, isClosing } = modalState
	const [formData, setFormData] = useState<ReferralFormData>(initialFormData)
	const [walletData, setWalletData] = useState<ReferralWalletData | null>(null)
	const hasWalletData = Boolean(walletData)
	const [lastReferralName, setLastReferralName] = useState<string>("")
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

	const transitionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const swapInTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	const clearTransitionTimers = useCallback(() => {
		if (transitionTimerRef.current) {
			clearTimeout(transitionTimerRef.current)
			transitionTimerRef.current = null
		}
		if (swapInTimerRef.current) {
			clearTimeout(swapInTimerRef.current)
			swapInTimerRef.current = null
		}
	}, [])

	const updateModalState = useCallback((patch: Partial<ModalState>) => {
		setModalState((currentModalState) => ({
			...currentModalState,
			...patch
		}))
	}, [])

	const handleStageChange = useCallback((nextStage: ReferralStage) => {
		clearTransitionTimers()
		updateModalState({ transitionPhase: "swapping-out" })

		transitionTimerRef.current = setTimeout(() => {
			updateModalState({ stage: nextStage, transitionPhase: "swapping-in" })

			swapInTimerRef.current = setTimeout(() => {
				updateModalState({ transitionPhase: "idle" })
			}, REFERRAL_SWAP_IN_DURATION_IN_MS)
		}, REFERRAL_SWAP_OUT_DURATION_IN_MS + REFERRAL_SWAP_GAP_IN_MS)
	}, [clearTransitionTimers, updateModalState])

	const handleClose = useCallback(() => {
		if (isClosing) {
			return
		}
		updateModalState({ isClosing: true })

		closeTimerRef.current = setTimeout(() => {
			setModalState(initialModalState)
			setFormData(initialFormData)
			setIsSubmitting(false)
			onClose()
		}, REFERRAL_SWAP_OUT_DURATION_IN_MS)
	}, [isClosing, onClose, updateModalState])

	const handleResetForm = useCallback(() => {
		setFormData(initialFormData)
	}, [])

	const handleGoToForm = useCallback(() => {
		handleStageChange("form")
	}, [handleStageChange])

	const handleGoToLink = useCallback(() => {
		handleStageChange("link")
	}, [handleStageChange])

	const handleFormDataChange = useCallback((partialFormData: Partial<ReferralFormData>) => {
		setFormData((currentFormData) => ({
			...currentFormData,
			...partialFormData
		}))
	}, [])

	const handleFetchWallet = useCallback(async () => {
		try {
			const wallet = await ReferralService.getWallet()
			setWalletData(wallet)
		} catch {
			setWalletData({
				walletTotal: 0,
				walletCount: 0,
				validityDays: 90,
				referrals: []
			})
		}
	}, [])

	const handleFormSubmit = useCallback(async () => {
		setIsSubmitting(true)

		try {
			const payload = buildCreateReferralPayload(formData, isAdmin)
			await ReferralService.createReferral(payload)

			setLastReferralName(formData.companyName)
			handleStageChange("done")
			setIsSubmitting(false)
		} catch (error) {
			triggerValidation(error as ErrorType)
			setIsSubmitting(false)
		}
	}, [formData, isAdmin, handleStageChange, triggerValidation])

	const handleIndicateAnother = useCallback(() => {
		handleResetForm()
		handleStageChange("form")
	}, [handleResetForm, handleStageChange])

	useEffect(() => {
		return () => {
			clearTransitionTimers()
			if (closeTimerRef.current) {
				clearTimeout(closeTimerRef.current)
			}
		}
	}, [clearTransitionTimers])

	useEffect(() => {
		if (currentStage === "done" && !hasWalletData) {
			handleFetchWallet()
		}
	}, [currentStage, hasWalletData, handleFetchWallet])

	const currentWidthInPixels = stageToWidthInPixels[currentStage] ?? 520

	const screenClassName = isClosing
		? classes.screenSwappingOut
		: transitionPhase === "swapping-out"
			? classes.screenSwappingOut
			: transitionPhase === "swapping-in"
				? classes.screenSwappingIn
				: classes.screenModalIn

	const isOfferStage = currentStage === "offer"
	const closeButtonClassName = isOfferStage
		? `${classes.closeButton} ${classes.closeButtonOnHeader}`
		: classes.closeButton

	const renderOfferScreen = (): React.ReactNode => {
		return (
			<ReferralOfferScreen
				isAdmin={isAdmin}
				onClaim={handleGoToForm}
				onClose={handleClose}
			/>
		)
	}

	const renderFormScreen = (): React.ReactNode => {
		return (
			<ReferralFormScreen
				isAdmin={isAdmin}
				formData={formData}
				onFormDataChange={handleFormDataChange}
				onGoToLink={handleGoToLink}
				onSubmit={handleFormSubmit}
				isSubmitting={isSubmitting}
				apiValidationErrors={validation}
				clearValidation={clearValidation}
			/>
		)
	}

	const renderLinkScreen = (): React.ReactNode => {
		return (
			<ReferralLinkScreen
				referralLink={referralLink}
				onGoToForm={handleGoToForm}
				onClose={handleClose}
			/>
		)
	}

	const renderDoneScreen = (): React.ReactNode => {
		if (!walletData) {
			return (
				<Box className={classes.screenContainer}>
					<Typography variant="h6" className={classes.placeholderContent}>
						{"Carregando..."}
					</Typography>
				</Box>
			)
		}

		return (
			<ReferralDoneScreen
				isAdmin={isAdmin}
				leadName={lastReferralName}
				walletData={walletData}
				formData={formData}
				onIndicateAnother={handleIndicateAnother}
			/>
		)
	}

	const stageToRenderer: Record<ReferralStage, () => React.ReactNode> = {
		offer: renderOfferScreen,
		form: renderFormScreen,
		link: renderLinkScreen,
		done: renderDoneScreen
	}

	const renderCurrentScreen = (): React.ReactNode => {
		return stageToRenderer[currentStage]()
	}

	return (
		<Dialog
			open={open}
			onClose={(event, reason) => {
				if (reason === "backdropClick") {
					return
				}
				handleClose()
			}}
			PaperProps={{
				className: classes.dialogPaper,
				style: { width: currentWidthInPixels }
			}}
			BackdropProps={{
				style: {
					backgroundColor: ColorUtil.hexToRgba(colors.purple[950], 0.45),
					backdropFilter: "blur(1.5px)",
					WebkitBackdropFilter: "blur(1.5px)"
				}
			}}
			maxWidth={false}
			fullWidth={false}
		>
			<IconButton
				className={closeButtonClassName}
				onClick={handleClose}
				disableRipple
			>
				<Typography component="span" className={classes.closeButtonGlyph}>
					{"✕"}
				</Typography>
			</IconButton>
			<Box className={screenClassName}>
				{renderCurrentScreen()}
			</Box>
		</Dialog>
	)
}

export default ReferralPopup
